import * as React from 'react';
import withStyles from 'react-jss';
import { message } from 'src/design-system/components/atoms';
import Spin from 'src/design-system/components/atoms/Spin/Spin';
import Modal from 'src/design-system/components/atoms/Modal/Modal';
import * as moment from 'moment';
import { useTranslation } from 'react-i18next';
import FixedHeaderTable from 'src/design-system/components/molecules/FixedHeaderTable/FixedHeaderTable';
import { fetchConsignmentInvoices } from '../../../api/dashboard';
import { getInvoiceColumns, getPieceAndEwbDetailsColumns } from './masterdata';
import GalleryModal from 'src/design-system/components/molecules/GalleryModal/GalleryModal';
import styles from './styles';

interface PieceAndEwbNumberObject {
    piece_reference_number: string;
    ewb_number: string;
}

interface PieceAndEwbDetailsModalData {
    invoiceNumber: string;
    pieceAndEwbDetails: PieceAndEwbNumberObject[];
}

const ConsignmentInvoiceTable = (props: any) => {
    const { t } = useTranslation();

    const {
        referenceNumber,
        parentReferenceNumber,
        classes,
        theme,
    } = props;
    const [loading, setLoading] = React.useState(false);
    const [invoices, setInvoices] = React.useState([]);
    const [isGalleryModalVisible, setIsGalleryModalVisible] = React.useState(false);
    const [mediaList, setMediaList] = React.useState([]);
    const [pieceAndEwbDetailsModalVisible, setPieceAndEwbDetailsModalVisible] =
        React.useState(false);
    const [pieceAndEwbDetailsModalData, setPieceAndEwbDetailsModalData] =
        React.useState<PieceAndEwbDetailsModalData>({
            invoiceNumber: null,
            pieceAndEwbDetails: [],
        });

    React.useEffect(() => {
        fetchInvoices();
    }, [referenceNumber]);

    const fetchInvoices = async () => {
        if (!referenceNumber) {
            return;
        }
        setLoading(true);
        const response = await fetchConsignmentInvoices({
            reference_number: referenceNumber,
        });
        if (response.isSuccess) {
            setInvoices(response.data.invoices);
        } else {
            message.error(response.errorMessage);
        }
        setLoading(false);
    };
    const renderGalleryModal = (imageArray: any[]) => {
        if (!imageArray.length) {
            return 'Not Available';
        }
        return (
            <a onClick={() => {
                setMediaList(imageArray);
                setIsGalleryModalVisible(true);
            }}
            >
                {t('View')}
            </a>
        );
    };
    const renderPieceAndEwbDetailsModal = (pieceAndEwbDetails: any[], invoiceNumber: string) => {
        return (
            <a onClick={() => {
                setPieceAndEwbDetailsModalData({
                    invoiceNumber: invoiceNumber || null,
                    pieceAndEwbDetails,
                });
                setPieceAndEwbDetailsModalVisible(true);
            }}
            >
                {t('View')}
            </a>
        );
    };
    const getColumnsForInvoicesTable = () => {
        const columns = getInvoiceColumns({
            consignmentReferenceNumber: referenceNumber,
            parentReferenceNumber,
        })?.map((col) => {
            return (
                col && {
                    dataIndex: col.id,
                    title: col.pretty_name,
                    width: col.width || 80,
                    render: (x, record, index) => {
                        switch (col.id) {
                            case 'piece_reference_number':
                            case 'ewb_number':
                                const pieceAndEwbDetails = record['piece_and_ewb_details'] || [];
                                pieceAndEwbDetails.forEach((item, index) => {
                                    item.invoice_number = record['invoice_number'];
                                    item.s_no = index + 1;
                                });
                                if (!pieceAndEwbDetails.length) {
                                    return null;
                                }
                                if (pieceAndEwbDetails.length === 1) {
                                    return pieceAndEwbDetails[0][col.id];
                                }
                                return renderPieceAndEwbDetailsModal(pieceAndEwbDetails, record['invoice_number']);
                            case 'proof_image_list':
                                if (!record[col.id] || !record[col.id].length) {
                                    return t('not_available');
                                }
                                const parsedData = record[col.id].map(
                                    (item: string | { url: string }) => {
                                        // Handle both string URLs and object URLs from API
                                        return typeof item === 'string' ? item : item?.url;
                                    },
                                ).filter(Boolean);
                                return renderGalleryModal(parsedData);
                            case 'invoice_date':
                            case 'ewb_creation_time':
                            case 'ewb_expiry_time':
                                return (record[col.id] && !isNaN(record[col.id])) ?
                                    moment(Number(record[col.id])).format('DD/MM/YYYY, HH:mm') :
                                    null;
                            default:
                                return x;
                        }
                    },
                }
            );
        });
        return columns.filter((column) => column);
    };
    const generatePieceAndEwbDetailsColumns = () => {
        const columns = getPieceAndEwbDetailsColumns({
            consignmentReferenceNumber: referenceNumber,
            parentReferenceNumber,
        })?.map((col) => {
            return (
                col && {
                    dataIndex: col.id,
                    title: col.pretty_name,
                    width: col.width || 80,
                    render: (x, record, index) => {
                        switch (col.id) {
                            case 's_no':
                                return index + 1;
                            case 'invoice_number':
                                return pieceAndEwbDetailsModalData.invoiceNumber || 'Not Available';
                            default:
                                return x || 'N/A';
                        }
                    },
                }
            );
        });
        return columns.filter((column) => column);
    };
    return (
        <>
            {loading && <Spin />}
            {!loading && (<FixedHeaderTable
                locale={{ emptyText: 'No Invoices Found' }}
                rowKey={(_record: any, index: number) => index}
                dataSource={invoices}
                pagination={false}
                columns={getColumnsForInvoicesTable()}
            />)}
            {isGalleryModalVisible && (
                <GalleryModal
                    isVisible={isGalleryModalVisible}
                    mediaList={mediaList}
                    handleModalClose={() => {
                        setIsGalleryModalVisible(false);
                        setMediaList([]);
                    }}
                />
            )}
            {pieceAndEwbDetailsModalVisible && (
                <Modal
                    title= {`Invoice: ${pieceAndEwbDetailsModalData.invoiceNumber}`}
                    bodyStyle={{
                        padding: theme.spacing[0],
                    }}
                    width={900}
                    visible={pieceAndEwbDetailsModalVisible}
                    footer={null}
                    onCancel={() => {
                        setPieceAndEwbDetailsModalVisible(false);
                        setPieceAndEwbDetailsModalData({
                            invoiceNumber: null,
                            pieceAndEwbDetails: [],
                        });
                    }}
                    destroyOnClose
                    >
                        <div className={classes.tableContainer}>
                        <FixedHeaderTable
                            locale={{ emptyText: 'No pieces' }}
                            rowKey={(_record: any, index: number) => index}
                            pagination={false}
                            dataSource={pieceAndEwbDetailsModalData.pieceAndEwbDetails}
                            columns={generatePieceAndEwbDetailsColumns()}
                        />
                        </div>
                </Modal>
            )}
        </>
    );
};

export default withStyles(styles, { injectTheme: true })(ConsignmentInvoiceTable);
